import React from "react";

type Column<T> = {
  label: string | React.ReactNode;
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
  children?: Column<T>[]; // 👈 allow sub-columns
};

interface ListViewProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (item: T) => void;
  mainDivClassName?: string;
  mailDivClassName?: string;
  trClassName?: string;
  tdClassName?: string;
  thClassName?: string;
  ActionClassName?: string;
  renderExpandedContent?: (data: T) => React.ReactNode; // 👈 Optional expanded content
  expandedRows?: Set<number>; // 👈 Track expanded rows
  scrollbarClassName?: string;
}


function ListView<T>({
  data,
  columns,
  onRowClick,
  mailDivClassName = "p-0 rounded-3xl",
  mainDivClassName = "bg-[#F7F6F2] rounded-3xl",
  trClassName = "hover:bg-[#FBD037]/10 group transition-colors duration-150",
  thClassName = "",
  tdClassName = "",
  ActionClassName,
  renderExpandedContent,
  expandedRows,
  scrollbarClassName = "hide-scrollbar",
}: ListViewProps<T>) {
  const hasGroups = columns.some((c) => c.children && c.children.length > 0);
  const hasExpandedContent = !!renderExpandedContent;

  return (
    <div className={`${mainDivClassName}`}>
      <div className={`overflow-auto bg-[#FCFDF8] max-h-[90vh] ${scrollbarClassName} ${mailDivClassName}`}>
        <table className="w-full text-sm whitespace-nowrap">
          <thead
            className={`text-[#131327]/70 border-b border-dashed border-gray-300 sticky top-0 z-20 shadow-sm bg-[#FCFDF8] ${thClassName}`}
          >

            {/* Top row */}
            <tr>
              {columns.map((col, i) => {
                const isGroup = col.children && col.children.length > 0;

                const isStatusOrShelve = typeof col?.label === "string" &&
                  (col.label === "Status" || col.label === "Bin Location");

                return (
                  <th
                    key={i}
                    colSpan={isGroup ? col.children!.length : 1}
                    rowSpan={isGroup ? 1 : hasGroups ? 2 : 1}
                    className={`font-semibold px-4 py-2 border-r last:border-none border-dashed border-gray-300 select-none
                      ${isGroup ? "text-center border-b border-dashed" : "text-center"} 
                      ${isStatusOrShelve ? "text-center" : ""} 
                      ${col.label === "Action" ? "text-center" : ""}
                    `}
                  >
                    {col.label}
                  </th>
                );
              })}
            </tr>

            {/* Second row for grouped subheaders */}
            {hasGroups && (
              <tr>
                {columns.flatMap((col, i) =>
                  col.children
                    ? col.children.map((child, j) => (
                      <th
                        key={`${i}-${j}`}
                        className="text-center font-normal px-4 py-1.5 border-r border-dashed border-gray-300 text-[#131327]/70"
                      >
                        {child.label}
                      </th>
                    ))
                    : []
                )}
              </tr>
            )}
          </thead>

          <tbody className="divide-y divide-gray-100 bg-transparent">
            {Array.isArray(data) && data.map((item, idx) => {
              const key = (item as any)?.id?.toString?.() ?? idx.toString();
              const idNum = parseInt(key, 10); // 👈 Parse for consistency
              const isExpanded = hasExpandedContent && expandedRows?.has(idNum);

              return (
                <React.Fragment key={key}>
                  {/* Main row */}
                  <tr
                    onClick={() => onRowClick?.(item)}
                    className={`${trClassName} ${isExpanded ? "bg-gray-50" : ""}`}
                  >
                    {columns.flatMap((col, i) => {
                      if (col.children && col.children.length > 0) {
                        return col.children.map((child, j) => (
                          <td
                            key={`${i}-${j}`}
                            className={`px-6 py-3 text-[#212121] text-xs ${tdClassName}`}
                          >
                            {child.render ? child.render(item, idx) : null}
                          </td>
                        ));
                      }
                      return (
                        <td
                          key={i}
                          className={`px-6 py-3 text-[#212121] text-center text-xs ${tdClassName}`}
                        >
                          {col.render ? col.render(item, idx) : null}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Expanded content row */}
                  {isExpanded && renderExpandedContent && (
                    <tr>
                      <td
                        colSpan={
                          (hasExpandedContent ? 1 : 0) +
                          columns.reduce(
                            (total, col) =>
                              total + (col.children ? col.children.length : 1),
                            0
                          )
                        }
                        className="p-0 border-b border-gray-200"
                      >
                        {renderExpandedContent(item)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ListView;

